import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import Seo from '../../components/Seo';
import {
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  ClipboardList,
  FileText,
  AlertTriangle,
  Clock,
} from 'lucide-react';

const Breadcrumbs = ({ label }) => (
  <nav aria-label="Breadcrumb" className="text-sm text-white/80 mb-6" data-testid="breadcrumbs">
    <ol className="flex flex-wrap items-center gap-1">
      <li><Link to="/" className="hover:text-[#F9A825]">Home</Link></li>
      <li className="text-white/50">/</li>
      <li><Link to="/healthcare-partners" className="hover:text-[#F9A825]">Healthcare Partners</Link></li>
      <li className="text-white/50">/</li>
      <li className="text-[#F9A825] font-semibold">{label}</li>
    </ol>
  </nav>
);

/**
 * PartnerPageTemplate
 *
 * Required props:
 *  - seo: { title, description, canonical, schemaId, schema }
 *  - eyebrow: short uppercase tag string
 *  - heading: H1
 *  - intro: paragraph after H1
 *  - audience: { title, description, bullets: string[] }        // Who is this for
 *  - problem: { title, description, bullets: string[] }         // Problem we solve
 *  - capabilities: { title, items: [{icon, title, desc}] }      // Transportation capabilities
 *  - coordination: { title, steps: [{title, desc}] }            // How coordination works
 *  - why: { title, items: [{icon, title, desc}] }               // Why MoveraCare
 *  - transportRequest: { title, description, cta: {label, to} } // How to submit request
 *  - vendorRelationship: { title, description }                 // How to start provider relationship
 *  - testid: page-level test id
 */
const PartnerPageTemplate = ({
  seo,
  eyebrow,
  heading,
  intro,
  audience,
  problem,
  capabilities,
  coordination,
  why,
  transportRequest,
  vendorRelationship,
  testid,
}) => {
  const path = new URL(seo.canonical).pathname;
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Healthcare Partners', url: '/healthcare-partners' },
    { name: eyebrow, url: path },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid={testid}>
      <Seo
        title={seo.title}
        description={seo.description}
        path={path}
        schema={seo.schema}
        breadcrumbs={breadcrumbs}
      />
      {/* Hero */}
      <section className="py-16 md:py-20 bg-[#004C97]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <Breadcrumbs label={eyebrow} />
            <div className="inline-flex items-center gap-2 bg-[#F9A825] text-[#004C97] px-4 py-1.5 rounded-full text-sm font-bold mb-6" data-testid="page-eyebrow">
              <span>{eyebrow}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="page-heading">
              {heading}
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              {intro}
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <Link to={transportRequest.cta.to}>
                <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold text-lg px-8 shadow-lg" data-testid="hero-cta-request-transport">
                  {transportRequest.cta.label}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="/MoveraCare-Vendor-Procurement-Packet.pdf" target="_blank" rel="noopener noreferrer" download>
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#004C97] font-semibold text-lg px-8" data-testid="hero-cta-download-packet">
                  Download Provider / Vendor Packet
                </Button>
              </a>
              <a href="mailto:dispatch@moveracaremobility.com?subject=Provider%20Relations%20Inquiry">
                <Button size="lg" variant="outline" className="border-2 border-[#F9A825] text-[#F9A825] hover:bg-[#F9A825] hover:text-[#004C97] font-semibold text-lg px-8" data-testid="hero-cta-contact-provider-relations">
                  <Mail className="mr-2 w-5 h-5" />
                  Provider Relations
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Who is this for? */}
      <section className="py-16 bg-white" data-testid="section-audience">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WHO THIS IS FOR</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] leading-tight">{audience.title}</h2>
            </div>
            <div className="lg:col-span-2">
              <p className="text-gray-700 leading-relaxed mb-4">{audience.description}</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {audience.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Problem we solve */}
      <section className="py-16 bg-gray-50" data-testid="section-problem">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">THE PROBLEM WE SOLVE</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] leading-tight">{problem.title}</h2>
            </div>
            <div className="lg:col-span-2">
              <p className="text-gray-700 leading-relaxed mb-4">{problem.description}</p>
              <ul className="space-y-2">
                {problem.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-[#F9A825] font-bold flex-shrink-0">→</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Transportation capabilities */}
      <section className="py-16 bg-white" data-testid="section-capabilities">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">TRANSPORTATION CAPABILITIES</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] leading-tight">{capabilities.title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {capabilities.items.map((item) => {
              const Icon = item.icon;
              return (
                <Card key={item.title} className="border-2 hover:border-[#F9A825] transition-all">
                  <CardHeader>
                    <div className="w-11 h-11 rounded-lg bg-[#F9A825] flex items-center justify-center mb-2">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <CardTitle className="text-[#004C97] text-base leading-tight">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coordination workflow */}
      <section className="py-16 bg-[#004C97]" data-testid="section-coordination">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">HOW COORDINATION WORKS</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white">{coordination.title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {coordination.steps.map((s, idx) => (
              <div key={s.title} className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-5 hover:border-[#F9A825] transition-colors">
                <div className="w-9 h-9 rounded-full bg-[#F9A825] text-[#004C97] flex items-center justify-center font-bold text-sm mb-3">{idx + 1}</div>
                <h3 className="text-white font-semibold text-base mb-2 leading-tight">{s.title}</h3>
                <p className="text-gray-200 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why MoveraCare */}
      <section className="py-16 bg-gray-50" data-testid="section-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WHY MOVERACARE</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97]">{why.title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {why.items.map((w) => {
              const Icon = w.icon;
              return (
                <div key={w.title} className="flex items-start gap-3 p-5 bg-white border-2 border-gray-100 rounded-xl hover:border-[#F9A825] transition-all">
                  <div className="w-10 h-10 rounded-lg bg-[#F9A825]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-[#F9A825]" />
                  </div>
                  <div>
                    <h3 className="text-[#004C97] font-semibold mb-1 leading-tight">{w.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{w.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Submit a transportation request + Vendor relationship */}
      <section className="py-16 bg-white" data-testid="section-request-and-vendor">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-2xl border-2 border-[#F9A825] bg-[#FFF8E7] p-8">
              <ClipboardList className="w-10 h-10 text-[#F9A825] mb-3" />
              <h3 className="text-2xl font-bold text-[#004C97] mb-2 leading-tight">{transportRequest.title}</h3>
              <p className="text-gray-700 leading-relaxed mb-5">{transportRequest.description}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to={transportRequest.cta.to}>
                  <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold w-full sm:w-auto" data-testid="cta-transport-request">
                    {transportRequest.cta.label}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <a href="tel:+18329252128">
                  <Button size="lg" variant="outline" className="border-2 border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white font-semibold w-full sm:w-auto" data-testid="cta-transport-call">
                    <Phone className="mr-2 w-4 h-4" />
                    (832) 925-2128
                  </Button>
                </a>
              </div>
            </div>

            <div className="rounded-2xl border-2 border-[#004C97] bg-[#F5F8FD] p-8">
              <FileText className="w-10 h-10 text-[#004C97] mb-3" />
              <h3 className="text-2xl font-bold text-[#004C97] mb-2 leading-tight">{vendorRelationship.title}</h3>
              <p className="text-gray-700 leading-relaxed mb-5">{vendorRelationship.description}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="/MoveraCare-Vendor-Procurement-Packet.pdf" target="_blank" rel="noopener noreferrer" download>
                  <Button size="lg" className="bg-[#004C97] text-white hover:bg-[#003D7A] font-semibold w-full sm:w-auto" data-testid="cta-vendor-download">
                    Download Vendor Packet
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </a>
                <Link to="/procurement">
                  <Button size="lg" variant="outline" className="border-2 border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white font-semibold w-full sm:w-auto" data-testid="cta-vendor-procurement">
                    View Procurement &amp; Credentialing
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service area strip */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-3 sm:gap-4 bg-white border border-gray-200 rounded-full px-5 py-3 shadow-sm">
            <MapPin className="w-5 h-5 text-[#F9A825]" />
            <p className="text-sm text-gray-700">
              <strong className="text-[#004C97]">Greater Houston Service Area</strong> · Harris · Fort Bend · Brazoria · Montgomery · Galveston
            </p>
            <Clock className="w-4 h-4 text-[#F9A825] hidden sm:inline" />
            <span className="text-xs text-gray-600 hidden sm:inline">Mon–Sat 6:00 AM – 7:00 PM</span>
          </div>
        </div>
      </section>

      {/* Non-emergency disclaimer */}
      <section className="py-8 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-100 border-l-4 border-gray-400 rounded p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-700 leading-relaxed">
              MoveraCare provides <strong>non-emergency medical transportation</strong>. We do not provide ambulance, 911, emergency response, stretcher ambulance, or oxygen therapy services. <strong>Not for medical emergencies. Call 911 in an emergency.</strong>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnerPageTemplate;
